import React from "react";
import { Box, HStack, Icon, Text } from "@chakra-ui/react";
import { FiAward } from "react-icons/fi";
import { motion, useReducedMotion } from "framer-motion";
import { alpha, useGovTheme } from "./useGovTheme";

/**
 * One choice in a tally: its label, the real numbers, and a bar that grows to its share. The leading
 * choice is lit (and gets a small award icon only once the result is final). The numbers are always
 * written beside the bar in 14px or larger — the bar is a picture of them, never the only copy. Added 2026-09-12.
 */
export function GovTallyBar({ label, meta, percent, leading, final }: { label: string; meta: string; percent: number; leading: boolean; final: boolean }) {
  const t = useGovTheme();
  const reduce = useReducedMotion();
  const pct = Math.max(0, Math.min(100, percent));
  const colour = leading ? (final ? t.success : t.primary) : t.neutral;
  return (
    <Box>
      <HStack justify="space-between" align="baseline" spacing={3} mb={1.5} flexWrap="wrap">
        <HStack spacing={1.5} minW={0}>
          {leading && final && <Icon as={FiAward} color={t.success} boxSize={4} flexShrink={0} />}
          <Text fontSize="md" fontWeight={leading ? "bold" : "medium"} color={t.text} wordBreak="break-word">{label}</Text>
        </HStack>
        <Text fontSize="sm" color={t.muted}>{meta}</Text>
      </HStack>
      <Box h="14px" borderRadius="full" bg={alpha(t.neutral, 0.18)} overflow="hidden" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct)} aria-label={`${label}: ${Math.round(pct)}%`}>
        <motion.div style={{ height: "100%", borderRadius: 9999, background: leading ? `linear-gradient(90deg, ${colour}, ${alpha(colour, 0.7)})` : colour }}
          initial={{ width: reduce ? `${pct}%` : "0%" }} animate={{ width: `${pct}%` }} transition={{ duration: reduce ? 0 : 0.9, ease: [0.22, 1, 0.36, 1] }} />
      </Box>
    </Box>
  );
}
